// admin/src/lib/book-form.js — the pure book <-> form bridge for the friendly rate-card
// editor. The Form view edits a deep clone of the assembled book (the loadBook shape); on
// Save it SERIALIZES back to the SAME body the JSON view PUTs, so both views drive the one
// route (PUT /v1/admin/books/:version -> replaceDraft -> insertMains + lintBook). Nothing
// here validates — the seam's book-lint is the single source of truth; this only shapes.
//
// Serialize discipline (so a form Save can never 500 the numeric columns):
//   credits_per_usd -> a number (blank/garbage -> 0, which lint flags as "must be positive")
//   sort            -> an integer (blank -> 0, matching insertMains' `m.sort || 0`)
//   every other number field (fixed_credits / factor / extra_fixed_credits / included_qty)
//                   -> a number or NULL (blank -> null; the columns are nullable and lint
//                      catches a null where the shape needs a value, e.g. the NULL-factor atom)
//   a cost_plus main carries NO fixed_credits / per_unit (they're fixed-main knobs) — nulled
//   match           -> an object with only the non-empty matcher arrays

export const MODE_OPTIONS = ['fixed', 'cost_plus'];
export const POSITION_OPTIONS = ['included', 'extra'];
export const EXTRA_MODE_OPTIONS = ['', 'fixed', 'cost_plus'];
// the per_unit vocabulary price.mainUnits() understands (book-lint KNOWN_PER_UNIT); anything
// else lint refuses — so the form offers exactly these + null.
export const PER_UNIT_OPTIONS = ['', 'per_15s_block', 'per_language'];
// the four matcher families (book-lint / seed-book): quote-side line_kinds, settle-side the rest
export const MATCH_KEYS = ['line_kinds', 'providers', 'models', 'label_prefixes'];

const anchorNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const intOr0 = (v) => { const n = Number(v); return Number.isInteger(n) ? n : 0; };
export const numOrNull = (v) => { if (v === '' || v == null) return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
export const arrFromCsv = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
export const csvFromArr = (a) => (Array.isArray(a) ? a.join(', ') : '');

export const cloneBook = (book) => JSON.parse(JSON.stringify(book));

function buildMatch(match) {
  const out = {};
  for (const k of MATCH_KEYS) {
    const v = match && match[k];
    if (Array.isArray(v) && v.length) out[k] = v.slice();
  }
  return out;
}

// serializeBook(draft) -> the assembled-book body ({ credits_per_usd, note, mains:[...] }).
export function serializeBook(draft) {
  return {
    credits_per_usd: anchorNum(draft.credits_per_usd),
    note: draft.note ? String(draft.note) : null,
    mains: (draft.mains || []).map((m) => {
      const fixed = m.mode === 'fixed';
      return {
        key: String(m.key || '').trim(),
        label: String(m.label || '').trim(),
        mode: m.mode,
        fixed_credits: fixed ? numOrNull(m.fixed_credits) : null,
        per_unit: fixed ? (m.per_unit || null) : null,
        sort: intOr0(m.sort),
        subs: (m.subs || []).map((s) => ({
          key: String(s.key || '').trim(),
          label: String(s.label || '').trim(),
          match: buildMatch(s.match || {}),
          position: s.position,
          factor: numOrNull(s.factor),
          extra_mode: s.extra_mode || null,
          extra_fixed_credits: numOrNull(s.extra_fixed_credits),
          included_qty: numOrNull(s.included_qty),
          unit: s.unit ? String(s.unit) : null,
          settle_on_fail: s.settle_on_fail === true,
          sort: intOr0(s.sort),
        })),
      };
    }),
  };
}

// two books are the same DRAFT content iff they serialize identically (drops status/version/
// effective_at — the fields a draft edit can't touch). Powers the editor's dirty flag.
export function sameContent(a, b) {
  return JSON.stringify(serializeBook(a)) === JSON.stringify(serializeBook(b));
}

export function blankMain(sort = 0) {
  return { key: '', label: '', mode: 'fixed', fixed_credits: 0, per_unit: null, sort, subs: [] };
}
export function blankSub(sort = 0) {
  return {
    key: '', label: '', position: 'included', factor: null, extra_mode: null,
    extra_fixed_credits: null, included_qty: null, unit: null, settle_on_fail: false, sort, match: {},
  };
}
